import { http } from '../http';
import { extrairMensagemErro } from '../../utils/apiError';

/**
 * Novidades da carteira (at-53, item 45) — a caixa de entrada in-app do
 * consultor: veículo que entrou na oficina, mudou de etapa, ficou pronto ou foi
 * entregue.
 *
 * Canal in-app apenas (decisão de Victor, 07/09/2026): sem e-mail e sem
 * WhatsApp no MVP.
 *
 * ⚠️ `titulo` e `detalhe` chegam PRONTOS do backend (convenção at-24/at-32) — a
 * tela não monta texto nem replica a regra de o que é novidade.
 */

export type TipoNovidade =
  | 'OFICINA_ENTRADA'
  | 'OFICINA_ETAPA'
  | 'OFICINA_PRONTO'
  | 'OFICINA_ENTREGUE';

export type StatusNovidadeFiltro = 'NAO_LIDAS' | 'TODAS';

export interface NovidadeDaCarteira {
  id: string;
  tipo: TipoNovidade;
  placa: string | null;
  carroId: string | null;
  titulo: string;
  detalhe: string | null;
  criadoEm: string;
  /** `null` = ainda não lida. */
  lidoEm: string | null;
}

export interface NovidadesDaCarteira {
  totalItems: number;
  page: number;
  limit: number;
  /** GLOBAL — não acompanha a paginação nem o filtro; é o número do badge. */
  naoLidas: number;
  items: NovidadeDaCarteira[];
}

const VAZIO: NovidadesDaCarteira = {
  totalItems: 0,
  page: 1,
  limit: 10,
  naoLidas: 0,
  items: [],
};

/**
 * ⚠️ **Não lança.** Novidade é informação acessória: se a chamada falhar, a
 * carteira precisa abrir do mesmo jeito, sem toast de erro e sem seção
 * quebrada. Mesmo critério das funções de reconciliação da at-32.
 */
export const fetchNovidades = async (
  filtros: { page?: number; limit?: number; status?: StatusNovidadeFiltro } = {},
): Promise<NovidadesDaCarteira> => {
  try {
    const { data } = await http.get<NovidadesDaCarteira>('/carteira/novidades', {
      params: {
        page: filtros.page ?? 1,
        limit: filtros.limit ?? 10,
        ...(filtros.status ? { status: filtros.status } : {}),
      },
    });

    return data ?? VAZIO;
  } catch {
    return VAZIO;
  }
};

/** Esta, sim, lança: foi o usuário que clicou e ele precisa saber se não funcionou. */
export const marcarNovidadeLida = async (id: string): Promise<void> => {
  try {
    await http.patch(`/carteira/novidades/${id}/lida`);
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Não foi possível marcar a novidade como lida.'));
  }
};

export const marcarTodasNovidadesLidas = async (): Promise<number> => {
  try {
    const { data } = await http.patch<{ marcadas: number }>('/carteira/novidades/lidas');
    return data?.marcadas ?? 0;
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Não foi possível marcar as novidades como lidas.'));
  }
};
